import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  SlackChannelResult,
  SlackScopeType,
  SlackTeamIntegrationView,
  SlackUserIntegrationView,
} from '@this+that/models';
import log from 'loglevel';

import { SendSlackReplyBody } from '../models/slack';
import { createApiWithAuth } from '../utils/axios';
import { requireQueryData } from '../utils/requireQueryData';

let slackApi: ReturnType<typeof createApiWithAuth> | null = null;

const getSlackApi = () => {
  if (!slackApi) {
    const baseUrl = import.meta.env.VITE_SLACK_BASE_URL;
    if (!baseUrl) {
      throw new Error('Slack base URL is not set');
    }
    slackApi = createApiWithAuth(baseUrl);
  }
  return slackApi;
};

export const getSlackIntegrationUrl = async (isOnboarding: boolean = false) => {
  try {
    const { data } = await getSlackApi().get('/oauth-url', {
      params: {
        scope_type: SlackScopeType.USER,
        isOnboarding,
      },
    });
    return data;
  } catch (error) {
    log.error('Error fetching authentication data:', error);
    throw error;
  }
};

export const getSlackBotIntegrationUrl = async () => {
  try {
    const { data } = await getSlackApi().get('/oauth-url', {
      params: {
        scope_type: SlackScopeType.BOT,
      },
    });
    return data;
  } catch (error) {
    log.error('Error fetching Slack bot authentication data:', error);
    throw error;
  }
};

interface DisconnectSlackIntegrationParams {
  deleteData: boolean;
  teamId?: string;
  workspaceId?: string;
}

export const disconnectSlackIntegration = async ({
  deleteData,
  teamId,
  workspaceId,
}: DisconnectSlackIntegrationParams) => {
  try {
    await getSlackApi().post('/unsubscribe', {
      teamId,
      workspaceId,
      deleteData,
    });
  } catch (error) {
    log.error('Error unsubscribing slack:', error);
    throw error;
  }
};

interface SlackInitialSyncResult {
  workspaceId?: string;
}

export const scheduleSlackInitialSync = async (
  teamId: string | undefined,
  code: string,
  state: string | null,
  scopeType: SlackScopeType = SlackScopeType.USER,
  isOnboarding: boolean = false,
): Promise<SlackInitialSyncResult> => {
  try {
    const { data } = await getSlackApi().post('/initial-sync', {
      teamId,
      code,
      state,
      scopeType,
      isOnboarding,
    });
    // Only the bot scope links a workspace; the user scope answers with a status string.
    return { workspaceId: data?.message?.workspaceId };
  } catch (error) {
    log.error('Error scheduling initial synchronization:', error);
    throw error;
  }
};

// A runaway guard, not a workspace-size limit: at the default page size it allows 20,000
// channels, so a real listing terminates long before it and only a broken cursor hits it.
const MAX_CHANNEL_PAGES = 200;
// The server hands the cursor back unchanged when an invocation ran out of budget before it
// could fetch. Retrying that page is the intended path; repeating it forever is not.
const MAX_SAME_CURSOR_RETRIES = 3;

export interface GetSlackChannelsParams {
  teamId?: string;
  workspaceId?: string;
  includeLastMessage?: boolean;
  cursor?: string;
  limit?: number;
}

export interface SlackChannelPage {
  channels: SlackChannelResult[];
  nextCursor?: string;
  hasMore: boolean;
}

export const getSlackChannelsPage = async ({
  teamId,
  workspaceId,
  includeLastMessage = false,
  cursor,
  limit,
}: GetSlackChannelsParams): Promise<SlackChannelPage> => {
  try {
    const { data } = await getSlackApi().get('/get-channels', {
      params: { teamId, workspaceId, includeLastMessage, cursor, limit },
    });

    const envelope = data?.message;

    return {
      channels: envelope?.channels ?? [],
      nextCursor: envelope?.nextCursor,
      hasMore: Boolean(envelope?.hasMore),
    };
  } catch (error) {
    log.error('Error fetching channels:', error);
    throw error;
  }
};

export const drainSlackChannels = async (
  params: GetSlackChannelsParams,
  onProgress?: (channels: SlackChannelResult[]) => void,
): Promise<SlackChannelResult[]> => {
  const channels: SlackChannelResult[] = [];
  let cursor = params.cursor;
  let sameCursorRetries = 0;

  for (let page = 0; page < MAX_CHANNEL_PAGES; page += 1) {
    const result = await getSlackChannelsPage({ ...params, cursor });
    channels.push(...result.channels);
    onProgress?.([...channels]);

    if (!result.hasMore) {
      return channels;
    }

    // Callers diff this list against the bot's memberships, so a listing that stopped short
    // reads as a removal. A drain that could not finish must fail, never return partial.
    if (!result.nextCursor) {
      throw new Error('Slack channel listing has no cursor to resume from');
    }

    sameCursorRetries =
      result.nextCursor === cursor ? sameCursorRetries + 1 : 0;

    if (sameCursorRetries > MAX_SAME_CURSOR_RETRIES) {
      throw new Error('Slack channel listing stalled on the same cursor');
    }

    cursor = result.nextCursor;
  }

  throw new Error('Slack channel listing did not terminate');
};

export const manageStackBots = async (
  teamId: string,
  workspaceId: string,
  joinChannelIds?: string[],
  removeChannelIds?: string[],
) => {
  try {
    const { data } = await getSlackApi().post('/manage-bot-in-channel', {
      teamId,
      workspaceId,
      joinChannelIds,
      removeChannelIds,
    });
    return data?.message;
  } catch (error) {
    log.error('Error managing stack bots:', error);
    throw error;
  }
};

const GET_TEAM_SLACK_INTEGRATIONS = gql`
  query getTeamSlackIntegrations($teamId: String!) {
    getTeamSlackIntegrations(teamId: $teamId) {
      teamId
      workspaceId
      workspaceName
      createdAt
      updatedAt
    }
  }
`;

const GET_USER_SLACK_INTEGRATIONS = gql`
  query getUserSlackIntegrations {
    getUserSlackIntegrations {
      userId
      workspaceId
      workspaceName
      userSlackId
      createdAt
      updatedAt
    }
  }
`;

interface GetTeamSlackIntegrationsData {
  getTeamSlackIntegrations: SlackTeamIntegrationView[];
}

interface GetUserSlackIntegrationsData {
  getUserSlackIntegrations: SlackUserIntegrationView[];
}

export function useGetTeamSlackIntegrations() {
  const { loading, refetch } = useQuery<GetTeamSlackIntegrationsData>(
    GET_TEAM_SLACK_INTEGRATIONS,
    {
      skip: true,
    },
  );

  const getTeamSlackIntegrations = useCallback(
    async (teamId: string) => {
      try {
        const data = requireQueryData(await refetch({ teamId }));

        return data.getTeamSlackIntegrations ?? [];
      } catch (error) {
        log.error('Error fetching team Slack integrations:', error);
        throw error;
      }
    },
    [refetch],
  );

  return { getTeamSlackIntegrations, loading };
}

export function useGetUserSlackIntegrations() {
  const { loading, refetch } = useQuery<GetUserSlackIntegrationsData>(
    GET_USER_SLACK_INTEGRATIONS,
    {
      skip: true,
    },
  );

  const getUserSlackIntegrations = useCallback(async () => {
    try {
      const data = requireQueryData(await refetch());

      return data.getUserSlackIntegrations ?? [];
    } catch (error) {
      log.error('Error fetching Slack integrations:', error);
      throw error;
    }
  }, [refetch]);

  return { getUserSlackIntegrations, loading };
}

export const sendSlackReply = async ({
  workspaceId,
  channel,
  text,
  threadTs,
  attachments,
}: SendSlackReplyBody) => {
  try {
    const { data } = await getSlackApi().post('/reply', {
      workspaceId,
      channel,
      text,
      threadTs,
      attachments,
    });

    return data;
  } catch (error) {
    log.error('Error posting slack reply:', error);
    throw error;
  }
};

export const readSlackMessage = async ({
  workspaceId,
  channelId,
  messageTs,
  messageId,
}: {
  workspaceId: string;
  channelId: string;
  messageTs: string;
  messageId: string;
}) => {
  try {
    const { data } = await getSlackApi().post('/mark-message-as-read', {
      workspaceId,
      channelId,
      messageTs,
      messageId,
    });

    return data;
  } catch (error) {
    log.error('Error marking slack message as read:', error);
    throw error;
  }
};
